import { mockStaff } from '../data/mockData';

const STORAGE_KEY = 'safeguardpro_staff';

const buildName = (staff) => {
  if (staff.name) return staff.name;
  return [staff.firstName, staff.middleName, staff.lastName].filter(Boolean).join(' ').trim();
};

const normalizeCertificates = (staff) => {
  if (Array.isArray(staff.certificates) && staff.certificates.length) {
    return staff.certificates.map((cert) => ({
      id: cert.id || `cert-${Math.random().toString(36).slice(2, 8)}`,
      name: cert.name || 'Certificate',
      expiryDate: cert.expiryDate || cert.expiry || '',
      files: cert.files || []
    }));
  }
  if (Array.isArray(staff.documents) && staff.documents.length) {
    return staff.documents.map((doc) => ({
      id: doc.id || `cert-${Math.random().toString(36).slice(2, 8)}`,
      name: doc.name || 'Certificate',
      expiryDate: doc.expiry || '',
      files: doc.files || [],
      status: doc.status
    }));
  }
  return [];
};

const normalizeStaff = (staff) => {
  const name = buildName(staff);
  const resolveRoleKey = () => {
    if (staff.roleKey) return staff.roleKey;
    if (staff.accessRole) return staff.accessRole;
    if (staff.role_key) return staff.role_key;
    if (staff.accessScope === 'admin') return 'admin';
    if (staff.accessScope === 'inventory') return 'inventory';
    return 'teacher';
  };
  return {
    ...staff,
    id: staff.id || `staff-${Math.random().toString(36).slice(2, 8)}`,
    name,
    firstName: staff.firstName || name.split(' ')[0] || '',
    lastName: staff.lastName || name.split(' ').slice(1).join(' ') || '',
    address: Array.isArray(staff.address) ? staff.address : staff.addressLines || [],
    certificates: normalizeCertificates(staff),
    documents: staff.documents || [],
    sickEntries: staff.sickEntries || [],
    holidayEntries: staff.holidayEntries || [],
    sickDays: staff.sickDays || { used: 0, total: 10 },
    holidays: staff.holidays || { used: 0, total: 28 },
    programmes: staff.programmes || staff.programmeIds || [],
    loginEnabled: Boolean(staff.loginEnabled),
    email: (staff.email || '').trim(),
    loginEmail: (staff.loginEmail || staff.email || '').trim().toLowerCase(),
    accessScope: staff.accessScope || 'staff',
    roleKey: resolveRoleKey(),
    passwordHash: staff.passwordHash || staff.password_hash || null,
    passwordMustChange: Boolean(staff.passwordMustChange || staff.password_must_change),
    passwordResetTokenHash: staff.passwordResetTokenHash || staff.password_reset_token_hash || null,
    passwordResetExpiresAt: staff.passwordResetExpiresAt || staff.password_reset_expires_at || null,
    lastPasswordResetAt: staff.lastPasswordResetAt || staff.last_password_reset_at || null
  };
};

export default class StaffService {
  list() {
    if (typeof window === 'undefined') {
      return mockStaff.map((staff) => normalizeStaff(staff));
    }
    try {
      const raw = window.localStorage.getItem(STORAGE_KEY);
      if (!raw) {
        const seeded = mockStaff.map((staff) => normalizeStaff(staff));
        window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
        return seeded;
      }
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length) {
        return parsed.map((staff) => normalizeStaff(staff));
      }
      const seeded = mockStaff.map((staff) => normalizeStaff(staff));
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify(seeded));
      return seeded;
    } catch (error) {
      return mockStaff.map((staff) => normalizeStaff(staff));
    }
  }

  save(staffList) {
    if (typeof window === 'undefined') return;
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(staffList));
  }

  add(staff) {
    const next = [...this.list(), normalizeStaff(staff)];
    this.save(next);
    return next;
  }

  update(staff) {
    const next = this.list().map((item) => (item.id === staff.id ? normalizeStaff(staff) : item));
    this.save(next);
    return next;
  }

  getById(id) {
    return this.list().find((staff) => staff.id === id) || null;
  }
}
